import { BitkitBreadcrumb, BitkitList, BitkitPageHeader, createBitkitToast, rem } from '@bitrise/bitkit-v2';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import ConfirmAbortBuildDialog from '@/components/ConfirmAbortBuildDialog/ConfirmAbortBuildDialog';
import ConfirmationDialog from '@/components/Dialog/ConfirmationDialog/ConfirmationDialog';
import ErrorDialog from '@/components/Dialog/ErrorDialog/ErrorDialog';
import { CONTENT_MAX_WIDTH } from '@/components/page/ContentWrapper';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import StickyPageHeader from '@/components/page/PageHeader/StickyPageHeader';
import RestrictedPlanBuildUpsellDialog from '@/components/RestrictedPlanBuildUpsellDialog/RestrictedPlanBuildUpsellDialog';
import useRestrictedPlanBuildUpsell from '@/components/RestrictedPlanBuildUpsellDialog/useRestrictedPlanBuildUpsell';
import { CardVerificationContext } from '@/hooks/analytics/cardVerificationTracking';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { useDeleteBuildBitriseYml, useHasBuildBitriseYml } from '@/hooks/bitriseYml/useBitriseYml';
import useRebuildWorkflow from '@/hooks/useRebuildWorkflow';
import { isBuildFinished, isBuildNotFinished } from '@/models/Build';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import datadogRumCustomTiming from '@/utils/datadogRumCustomTiming';
import {
  abortBuildPath,
  basePathForApp,
  buildLogPath,
  ciOverviewPath,
  pipelineDetailsPagePath,
  projectBuildList,
  workflowEditorPath,
  workflowEditorPathWithConfig,
} from '@/utils/monolithApiRouteService';

import useExpiringRawLogUrl from '../../hooks/useExpiringRawLogUrl';
import ShowYmlDialog from '../ShowYmlDialog/ShowYmlDialog';
import BuildHeaderButtons from './BuildHeaderButtons';
import usePipelineByPipelineWorkflowId from './hooks/usePipelineByPipelineWorkflowId';

const CARD_VERIFICATION: CardVerificationContext = {
  source: 'build_details_page',
  reason: 'build_limit_reached',
  productName: 'ci',
};

const BuildHeader = () => {
  const { app, build, abilities, stack, trackingId, isViewedAsPublicPage, logViewVersion } = useBuildDetailsPageStore();
  const { canChangeSettings, canRebuild, canViewBuildLogs, canViewBitriseYml } = abilities;
  const isRemoteAccessAvailable = !stack.id.startsWith('agent-pool-');

  const { user } = useContext(GlobalPropsContext);

  const {
    isUpsellDialogOpen,
    onUpsellDialogClose,
    organizationSlug,
    isLoading: isUpsellLoading,
    guardBuildAction,
  } = useRestrictedPlanBuildUpsell();

  const { status: logUrlStatus, refetch: refetchLogUrl } = useExpiringRawLogUrl(
    app.slug,
    build.slug,
    isBuildFinished(build),
    isViewedAsPublicPage,
  );

  const [errorMessage, setErrorMessage] = useState<ReactNode>('');
  const [isShowYMLVisible, setShowYMLVisible] = useState(false);
  const [isDeleteLogConfirmVisible, setDeleteLogConfirmVisible] = useState(false);
  const [isDeleteYMLConfirmVisible, setDeleteYMLConfirmVisible] = useState(false);
  const [isAbortConfirmVisible, setAbortConfirmVisible] = useState(false);

  const {
    featureFlags: { account },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const enableBranchSwitching = account.enableBranchSwitching;

  const isUserLoggedIn = Boolean(user?.username);

  const workflowEditorUrl = enableBranchSwitching
    ? workflowEditorPathWithConfig(app.slug, {
        anchorParams: {
          workflow_id: build.triggeredWorkflow,
          ...(build.branch ? { branch: build.branch } : {}),
        },
      })
    : workflowEditorPath(app.slug, build.triggeredWorkflow);

  const pipelineNumber = usePipelineByPipelineWorkflowId(app.slug, build.pipelineWorkflowId);

  const breadcrumbItems = useMemo(() => {
    // The CI overview needs a session, so a logged-out visitor gets the trail without that step
    // rather than a crumb it cannot follow.
    const items = [
      ...(isUserLoggedIn
        ? [
            <BitkitBreadcrumb.Item key="ci" href={ciOverviewPath()}>
              Bitrise CI
            </BitkitBreadcrumb.Item>,
          ]
        : []),
      <BitkitBreadcrumb.Item key="app" href={basePathForApp(app.slug)}>
        {app.title}
      </BitkitBreadcrumb.Item>,
    ];

    if (pipelineNumber.has && !pipelineNumber.isError) {
      items.push(
        pipelineNumber.isLoading ? (
          <BitkitBreadcrumb.CurrentItem key="pipelineBuildDetails">
            <Skeleton width={rem(150)} height="24" />
          </BitkitBreadcrumb.CurrentItem>
        ) : (
          <BitkitBreadcrumb.Item
            key="pipelineBuildDetails"
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            href={pipelineDetailsPagePath(app.slug, pipelineNumber.id!)}
          >
            {`Pipeline build #${pipelineNumber.number}`}
          </BitkitBreadcrumb.Item>
        ),
      );
    }

    return items;
  }, [app, isUserLoggedIn, pipelineNumber]);

  // The pipeline crumb is a current item while it loads, and the stuck row appends the build title
  // as one too -- two current pages in the same trail. The title is the one that gives way, the
  // same way `PageHeaderBreadcrumb` skips it for a trail that already ends in a current item.
  const endsInCurrentItem = pipelineNumber.has && !pipelineNumber.isError && pipelineNumber.isLoading;

  useEffect(() => {
    datadogRumCustomTiming('buildDetails_v2', 'breadcrumbs');
  }, []);

  const { mutate: rebuild, isPending: isRebuildPending } = useRebuildWorkflow(
    {
      buildSlug: build.slug,
    },
    {
      onSuccess: ({ data }) => {
        if (data.results?.length === 1 && data.results[0].buildUrl) {
          window.location.href = data.results[0].buildUrl;
        }
        if (data.results && data.results?.length > 1) {
          window.location.href = projectBuildList(app.slug);
        }
      },
      onError: (error) => {
        if (error.response?.data.errorMsg) {
          setErrorMessage(error.response?.data.errorMsg);
        } else if (error.response?.data.results?.length) {
          const resultErrors: string[] = [];
          error.response?.data.results.forEach((result) => {
            if (result.status === 'error') {
              resultErrors.push(`${result.triggeredPipeline || result.triggeredWorkflow}: ${result.message}`);
            }
          });
          setErrorMessage(
            <>
              {resultErrors.map((err) => (
                <BitkitList key={JSON.stringify(err)}>
                  <BitkitList.Item>{err}</BitkitList.Item>
                </BitkitList>
              ))}
            </>,
          );
        } else {
          setErrorMessage(error.response?.data.message || error.message || 'Unknown error');
        }
      },
    },
  );

  const {
    mutate: abortBuild,
    isPending: isAbortBuildPending,
    error: abortBuildError,
  } = useMonolithApiMutation(abortBuildPath(build.slug), 'post', {
    onSuccess: () => setAbortConfirmVisible(false),
  });

  const {
    mutate: deleteLog,
    isPending: isDeleteLogPending,
    isError: isDeleteLogError,
    error: deleteLogError,
  } = useMonolithApiMutation(buildLogPath(build.slug), 'delete', {
    onSuccess: () => {
      setDeleteLogConfirmVisible(false);
      refetchLogUrl();
      createBitkitToast({
        messageText: `This build's logs were deleted successfully.`,
        titleText: 'Logs deleted',
        variant: 'success',
      });
    },
  });

  const { data: hasBitriseYml, isLoading: isLoadingHasBitriseYml } = useHasBuildBitriseYml({ buildSlug: build.slug });
  const {
    mutate: deleteYml,
    isPending: isDeleteYmlPending,
    isError: isDeleteYmlError,
    error: deleteYmlError,
  } = useDeleteBuildBitriseYml({ buildSlug: build.slug });
  const deleteBitriseYml = () => {
    deleteYml(undefined, {
      onSuccess: () => {
        setDeleteYMLConfirmVisible(false);
        createBitkitToast({
          messageText: `This build's configuration YAML was deleted successfully.`,
          titleText: 'Configuration YAML deleted',
          variant: 'success',
        });
      },
    });
  };

  const onRebuildClick = (isRemote: boolean) => guardBuildAction(() => rebuild({ isRemote }));

  const onDownloadLogClick = async () => {
    segmentTrack('Download Logs Button Clicked', {
      build_log_type: logViewVersion,
      page_load_id: trackingId,
      build_slug: build.slug,
      app_slug: app.slug,
    });

    const { data: url } = await refetchLogUrl();

    if (url) {
      window.location.assign(url);
    }
  };

  const onShowYMLClick = () => {
    setShowYMLVisible(true);
    segmentTrack('Show Bitrise Yml Button Clicked', {
      app_slug: app.slug,
      build_slug: build.slug,
      build_log_type: logViewVersion,
      page_load_id: trackingId,
    });
  };

  const isAbortable = isBuildNotFinished(build);
  const isPipelineBuild = build.pipelineWorkflowId;
  const isRebuildable = build.rebuildable;
  const isAbortButtonVisible = canRebuild && isAbortable;
  const isRebuildButtonVisible = canRebuild && !isAbortable && !isPipelineBuild;
  const isRebuildWithRemoteAccessButtonVisible = isRebuildButtonVisible && isRemoteAccessAvailable;
  const isLoadingLog = logUrlStatus === 'pending';
  const isGeneratingLog = logUrlStatus === 'generating';
  const isLogSupported = logUrlStatus !== 'not_supported';
  const isLogDeleted = logUrlStatus === 'deleted';
  const deleteConfigurationText = isPipelineBuild
    ? 'Are you sure you want to delete the configuration YAML for all builds and attempts in this Pipeline? This action is irreversible, and project members will no longer be able to view it.'
    : "Are you sure you want to delete this build's configuration YAML? This action is irreversible, and project members will no longer be able to view it.";

  const pageTitle = build.pipelineWorkflowId ? build.triggeredWorkflow : `Build #${build.buildNumber}`;

  return (
    <>
      <StickyPageHeader shouldSticky>
        {(isSticky) => (
          <BitkitPageHeader
            contentWidth={CONTENT_MAX_WIDTH}
            breadcrumb={
              <BitkitBreadcrumb>
                {/* The stuck row hides the heading and shows the title in the trail instead, so the
                    trail has to carry it there -- see BitkitPageHeader's `title` docs. */}
                {breadcrumbItems}
                {isSticky && !endsInCurrentItem && (
                  <BitkitBreadcrumb.CurrentItem>{pageTitle}</BitkitBreadcrumb.CurrentItem>
                )}
              </BitkitBreadcrumb>
            }
            isSticky={isSticky}
            showDivider={false}
            title={pageTitle}
          >
            <BuildHeaderButtons
              appSlug={app.slug}
              buildSlug={build.slug}
              abortButton={{
                isVisible: isAbortButtonVisible,
                isLoading: isAbortBuildPending,
                onClick: () => setAbortConfirmVisible(true),
              }}
              deleteLogButton={{
                isVisible: canChangeSettings && isLogSupported,
                isDisabled: isLogDeleted,
                isLoading: isLoadingLog || isGeneratingLog,
                onClick: () => setDeleteLogConfirmVisible(true),
              }}
              downloadLogButton={{
                isVisible: canViewBuildLogs && isLogSupported,
                isDisabled: isLogDeleted,
                isLoading: isLoadingLog || isGeneratingLog,
                onClick: onDownloadLogClick,
              }}
              rebuildButton={{
                isVisible: isRebuildButtonVisible,
                isDisabled: !isRebuildable,
                isLoading: isRebuildPending || isUpsellLoading,
                onClick: () => onRebuildClick(false),
              }}
              remoteAccessButton={{
                isVisible: isRebuildWithRemoteAccessButtonVisible,
                isDisabled: !isRebuildable,
                isLoading: isRebuildPending || isUpsellLoading,
                onClick: () => onRebuildClick(true),
              }}
              showYmlButton={{
                isVisible: canViewBitriseYml,
                isDisabled: !hasBitriseYml,
                isLoading: isLoadingHasBitriseYml,
                onClick: onShowYMLClick,
              }}
              deleteYmlButton={{
                isVisible: canChangeSettings,
                isDisabled: !hasBitriseYml,
                isLoading: isLoadingHasBitriseYml,
                onClick: () => setDeleteYMLConfirmVisible(true),
              }}
              workflowEditorButton={{
                href: workflowEditorUrl,
                isVisible: canChangeSettings,
              }}
              isSticky={isSticky}
            />
          </BitkitPageHeader>
        )}
      </StickyPageHeader>
      <ConfirmAbortBuildDialog
        type="build"
        isOpen={isAbortConfirmVisible}
        onConfirm={(abortReason) =>
          abortBuild({
            abort_reason: abortReason,
            abort_with_success: false,
            skip_notifications: true,
          })
        }
        error={abortBuildError?.message}
        onClose={() => setAbortConfirmVisible(false)}
      />
      <ErrorDialog
        bodyContent={errorMessage}
        isVisible={!!errorMessage}
        onClose={() => setErrorMessage('')}
        title="Failed to start Build(s)"
      />
      <ConfirmationDialog
        size="sm"
        title="Delete logs"
        text="Are you sure you want to delete this build's logs? Members of this project won’t have access to the logs anymore. This action cannot be undone."
        isVisible={isDeleteLogConfirmVisible}
        isInProgress={isDeleteLogPending}
        isError={isDeleteLogError}
        errorMessage={deleteLogError?.message}
        confirmText="Delete logs"
        onConfirm={() => deleteLog(undefined)}
        closeText="Cancel"
        onClose={() => setDeleteLogConfirmVisible(false)}
      />
      <ConfirmationDialog
        title="Delete configuration YAML"
        text={deleteConfigurationText}
        isVisible={isDeleteYMLConfirmVisible}
        isInProgress={isDeleteYmlPending}
        isError={isDeleteYmlError}
        errorMessage={deleteYmlError?.message}
        confirmText="Delete configuration YAML"
        onConfirm={deleteBitriseYml}
        closeText="Cancel"
        onClose={() => setDeleteYMLConfirmVisible(false)}
      />
      {canViewBitriseYml && (
        <ShowYmlDialog
          appSlug={app.slug}
          sourceType="build"
          buildSlug={build.slug}
          buildNumber={build.buildNumber}
          triggeredAt={build.triggeredAt}
          configDatastoreUpdatedAt={build.configDatastoreUpdatedAt}
          canRestoreYml={canChangeSettings && !app.usesRepositoryYml}
          isVisible={isShowYMLVisible}
          onClose={() => setShowYMLVisible(false)}
        />
      )}
      <RestrictedPlanBuildUpsellDialog
        isOpen={isUpsellDialogOpen}
        onClose={onUpsellDialogClose}
        organizationSlug={organizationSlug}
        cardVerification={CARD_VERIFICATION}
      />
    </>
  );
};

export default BuildHeader;
